import type { IInputMapping } from "../types";

type ActionListener = (action: string) => void;

/**
 * Unified input across keyboard, mouse, touch, gamepad.
 * Game code binds to action names, not raw keys.
 */
export class InputManager {
  private mappings = new Map<string, IInputMapping>();
  private listeners = new Set<ActionListener>();
  private touchStart: { x: number; y: number; t: number } | null = null;
  private readonly TAP_MAX_MS = 200;
  private readonly TAP_MAX_PX = 10;
  private readonly SWIPE_MIN_PX = 50;

  constructor() {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("touchstart", this.onTouchStart, { passive: true });
    window.addEventListener("touchend", this.onTouchEnd);
  }

  register(mapping: IInputMapping): void {
    this.mappings.set(mapping.action, mapping);
  }

  on(listener: ActionListener): void {
    this.listeners.add(listener);
  }

  off(listener: ActionListener): void {
    this.listeners.delete(listener);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const t = e.target as HTMLElement | null;
    if (t) {
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable) return;
    }
    for (const mapping of this.mappings.values()) {
      if (mapping.keys.includes(e.key)) {
        this.fire(mapping.action);
        return;
      }
    }
  };

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("touchstart", this.onTouchStart);
    window.removeEventListener("touchend", this.onTouchEnd);
    this.listeners.clear();
    this.mappings.clear();
  }

  private onTouchStart = (e: TouchEvent): void => {
    const t = e.touches[0];
    if (!t) return;
    this.touchStart = { x: t.clientX, y: t.clientY, t: performance.now() };
  };

  private onTouchEnd = (e: TouchEvent): void => {
    if (!this.touchStart) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - this.touchStart.x;
    const dy = t.clientY - this.touchStart.y;
    const dt = performance.now() - this.touchStart.t;
    const distance = Math.hypot(dx, dy);
    if (dt < this.TAP_MAX_MS && distance < this.TAP_MAX_PX) {
      this.fire("tap");
    } else if (distance > this.SWIPE_MIN_PX) {
      if (Math.abs(dx) > Math.abs(dy)) {
        this.fire(dx > 0 ? "swipe-right" : "swipe-left");
      } else {
        this.fire(dy > 0 ? "swipe-down" : "swipe-up");
      }
    }
    this.touchStart = null;
  };

  private fire(action: string): void {
    for (const listener of this.listeners) listener(action);
  }
}
